'use client';

import { UploadModal } from '@/components/ui/organisms/UploadModal';
import { Button, Chip, Link, Textarea } from '@heroui/react';
import { Icon } from '@iconify/react';
import { AnimatePresence, motion } from 'framer-motion';
import NextLink from 'next/link';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import {
    ALLOWED_DOCUMENT_TYPES,
    CHAT_INPUT_MESSAGES,
    DOCUMENT_UPLOAD_NAME,
    MIME_TYPES,
    UI_CONSTANTS,
} from '../constants';
import { useVoiceInput } from '../hooks/useVoiceInput';
import type { ChatInputProps, UploadedFile } from '../props';
import { chatInputEffectivePlaceholder } from '../utils/user-spend-limit-copy';
import { VoiceVisualizer } from './VoiceVisualizer';
import { DarkTooltip } from './dark-tooltip';

// Temporarily hides the send button — per explicit request, not a
// functionality change. Enter still submits the message via handleKeyDown.
const SHOW_SEND_BUTTON = false;

export function ChatInput({
    conversationId,
    onSendMessage,
    aboveInput,
    fileReader,
    suggestion,
    isLoading = false,
    placeholder = CHAT_INPUT_MESSAGES.PLACEHOLDER,
    previewText = null,
    className = '',
    showScrollButton = false,
    onScrollToBottom,
    handleStop,
    initialMessage = '',
    onFileUpload,
    uploadedFile: externalUploadedFile,
    onFileRemove: externalOnFileRemove,
    isExtracting = false,
    extractionProgress,
    showSmartRoutingToggle = true,
    smartRoutingEnabled: controlledSmartRoutingEnabled,
    setSmartRoutingEnabled: controlledSetSmartRoutingEnabled,
    usageLimit: usageLimitProp,
    trialStrip = null,
    compact = false,
    isCreditGatePaused = false,
    lockedReason = null,
    showVoiceInput = true,
    width = 675,
    height = 125,
}: ChatInputProps) {
    // A trial on credits shows its strip in place of the old counter (M4S-5895 step 27).
    const usageLimit = trialStrip ? null : usageLimitProp;
    const orgUsageLimitReached = usageLimit?.hasReachedLimit ?? false;
    // A locked conversation (staff View-as, M4S-5670 §4) disables the input
    // exactly as a reached limit does, and says why in place of the placeholder.
    const usageLimitReached = orgUsageLimitReached || isCreditGatePaused || Boolean(lockedReason);
    const usageUnit = usageLimit?.limitType === 'token' ? 'tokens' : 'queries';
    const usageRemaining = usageLimit ? Math.max(0, usageLimit.limit - usageLimit.currentCount) : 0;
    const effectivePlaceholder =
        lockedReason ||
        chatInputEffectivePlaceholder({
            placeholder,
            orgUsageLimitReached,
            compact,
            usageUnit,
        });
    const [message, setMessage] = useState('');
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);
    const [internalUploadedFile, setInternalUploadedFile] = useState<UploadedFile | null>(null);
    const [fallbackSmartRoutingEnabled, setFallbackSmartRoutingEnabled] = useState(false);
    const smartRoutingEnabled = controlledSmartRoutingEnabled ?? fallbackSmartRoutingEnabled;
    const setSmartRoutingEnabled =
        controlledSetSmartRoutingEnabled ?? setFallbackSmartRoutingEnabled;
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const cancelButtonRef = useRef<HTMLButtonElement>(null);
    const hasInitialized = useRef(false);
    const prevInitialMessage = useRef(initialMessage);

    const handleTranscribed = useCallback((text: string) => {
        setMessage((prev) => {
            const newMessage = prev ? `${prev} ${text}` : text;
            return newMessage.slice(0, UI_CONSTANTS.MAX_MESSAGE_LENGTH);
        });
    }, []);

    const {
        isRecording,
        isTranscribing,
        recordingDuration,
        analyserRef,
        startRecording,
        cancelRecording,
        stopAndTranscribe,
    } = useVoiceInput(handleTranscribed, conversationId);

    const isVoiceActive = isRecording || isTranscribing;

    const prevIsVoiceActive = useRef(false);

    useEffect(() => {
        if (!prevIsVoiceActive.current && isVoiceActive) {
            cancelButtonRef.current?.focus();
        } else if (prevIsVoiceActive.current && !isVoiceActive) {
            textareaRef.current?.focus();
        }
        prevIsVoiceActive.current = isVoiceActive;
    }, [isVoiceActive]);

    const uploadedFile =
        externalUploadedFile !== undefined ? externalUploadedFile : internalUploadedFile;
    const handleFileRemove = externalOnFileRemove || (() => setInternalUploadedFile(null));

    useEffect(() => {
        if (initialMessage !== prevInitialMessage.current || !hasInitialized.current) {
            setMessage(initialMessage);
            hasInitialized.current = true;
            prevInitialMessage.current = initialMessage;
        }
    }, [initialMessage]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (isLoading || isExtracting || usageLimitReached || !message.trim()) return;
        onSendMessage(message.trim(), { smartRoutingEnabled });
        setMessage('');
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        // Tab takes the suggested next message, as a terminal prompt does.
        if (e.key === 'Tab' && !e.shiftKey && suggestion && !message.trim()) {
            e.preventDefault();
            setMessage(suggestion);
            return;
        }
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit(e);
        }
    };

    const setUploadedFile = useCallback((file: File) => {
        setInternalUploadedFile({ file, name: file.name });
    }, []);

    const handleModalUpload = useCallback(
        async ({
            file,
            setError,
            setProgress,
        }: {
            file: FormData;
            setError: React.Dispatch<React.SetStateAction<string>>;
            setProgress: React.Dispatch<React.SetStateAction<number>>;
        }) => {
            if (usageLimitReached) {
                setError('Organization usage limit reached — upload is disabled.');
                return { hasError: true };
            }
            const uploadedFileFromForm = file.get(DOCUMENT_UPLOAD_NAME) as File;
            if (!uploadedFileFromForm) {
                setError('No file provided');
                return { hasError: true };
            }
            setProgress(50);
            if (onFileUpload) {
                const result = await onFileUpload(uploadedFileFromForm);
                setProgress(100);
                return result;
            }
            setUploadedFile(uploadedFileFromForm);
            setProgress(100);
            return { hasError: false };
        },
        [onFileUpload, setUploadedFile, usageLimitReached],
    );

    const onDrop = useCallback(
        async (acceptedFiles: File[]) => {
            setIsDragOver(false);
            if (usageLimitReached) return;
            if (fileReader) {
                if (acceptedFiles.length > 0) fileReader.onFiles(acceptedFiles);
                return;
            }
            if (acceptedFiles.length > 0) {
                const droppedFile = acceptedFiles[0];
                if (onFileUpload) {
                    await onFileUpload(droppedFile);
                } else {
                    setUploadedFile(droppedFile);
                }
            }
        },
        [fileReader, onFileUpload, setUploadedFile, usageLimitReached],
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        onDragEnter: () => setIsDragOver(true),
        onDragLeave: () => setIsDragOver(false),
        accept: fileReader
            ? undefined
            : ALLOWED_DOCUMENT_TYPES.reduce(
                  (acc, ext) => {
                      const mimeType = MIME_TYPES[ext];
                      if (mimeType) acc[mimeType] = [`.${ext}`];
                      return acc;
                  },
                  {} as Record<string, string[]>,
              ),
        noClick: true,
        noKeyboard: true,
        multiple: Boolean(fileReader),
        disabled: usageLimitReached,
    });

    const readerInput = useRef<HTMLInputElement>(null);
    const attachButton = fileReader ? (
        <>
            <DarkTooltip
                content="Attach a brief or lists: email, PDF, Word, Excel, CSV or a screenshot"
                size="sm"
                delay={400}
            >
                <Button
                    isIconOnly
                    variant="light"
                    radius="full"
                    size="sm"
                    className="h-8 w-8 min-w-8 shrink-0 text-default-400 hover:text-default-600"
                    onPress={() => {
                        if (usageLimitReached) return;
                        readerInput.current?.click();
                    }}
                    isLoading={fileReader.busy}
                    isDisabled={isLoading || fileReader.busy || usageLimitReached}
                    aria-label="Attach a brief or lists"
                >
                    <Icon icon="lucide:paperclip" width={18} height={18} />
                </Button>
            </DarkTooltip>
            <input
                ref={readerInput}
                type="file"
                multiple
                hidden
                accept={fileReader.accept.join(',')}
                onChange={(e) => {
                    const picked = Array.from(e.target.files ?? []);
                    e.target.value = '';
                    if (picked.length) fileReader.onFiles(picked);
                }}
            />
        </>
    ) : onFileUpload ? (
        <DarkTooltip content="Attach a file" size="sm" delay={400}>
            <Button
                isIconOnly
                variant="light"
                radius="full"
                size="sm"
                className="h-8 w-8 min-w-8 shrink-0 text-default-400 hover:text-default-600"
                onPress={() => {
                    if (usageLimitReached) return;
                    setShowUploadModal(true);
                }}
                isDisabled={isLoading || isExtracting || usageLimitReached}
                aria-label="Attach file"
            >
                <Icon icon="lucide:paperclip" width={18} height={18} />
            </Button>
        </DarkTooltip>
    ) : null;

    const textareaOrVisualizer = isVoiceActive ? (
        <VoiceVisualizer
            analyserRef={analyserRef}
            isTranscribing={isTranscribing}
            recordingDuration={recordingDuration}
        />
    ) : (
        <Textarea
            id="chat-input"
            ref={textareaRef}
            value={previewText ?? message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
                suggestion && !orgUsageLimitReached ? `${suggestion}   ⇥ Tab` : effectivePlaceholder
            }
            isReadOnly={!!previewText}
            isDisabled={usageLimitReached}
            aria-describedby="chat-input-description"
            minRows={1}
            maxRows={4}
            maxLength={UI_CONSTANTS.MAX_MESSAGE_LENGTH}
            classNames={{
                base: 'flex-1 min-w-0',
                inputWrapper:
                    'border-none shadow-none bg-transparent p-0 min-h-0 data-[hover=true]:bg-transparent group-data-[focus=true]:bg-transparent',
                input: 'py-1 px-0 text-small placeholder:text-default-400 resize-none',
                innerWrapper: 'min-h-0',
            }}
        />
    );

    const iconCluster = (
        <div className="flex shrink-0 items-center gap-1">
            {isVoiceActive ? (
                <>
                    <DarkTooltip content="Cancel" size="sm" delay={400}>
                        <Button
                            ref={cancelButtonRef}
                            isIconOnly
                            variant="light"
                            radius="full"
                            size="sm"
                            className="h-8 w-8 min-w-8 shrink-0 text-default-400 hover:text-default-600"
                            onPress={cancelRecording}
                            isDisabled={isTranscribing}
                            aria-label="Cancel recording"
                        >
                            <Icon icon="lucide:x" width={16} height={16} />
                        </Button>
                    </DarkTooltip>
                    <DarkTooltip content="Stop and transcribe" size="sm" delay={400}>
                        <Button
                            isIconOnly
                            variant="light"
                            radius="full"
                            size="sm"
                            className="h-8 w-8 min-w-8 shrink-0 text-default-400 hover:text-default-600"
                            onPress={stopAndTranscribe}
                            isLoading={isTranscribing}
                            aria-label="Stop recording and transcribe"
                        >
                            <Icon icon="lucide:check" width={16} height={16} />
                        </Button>
                    </DarkTooltip>
                </>
            ) : (
                <>
                    {showVoiceInput && (
                        <DarkTooltip content="Voice input" size="sm" delay={400}>
                            <Button
                                isIconOnly
                                variant="light"
                                radius="full"
                                size="sm"
                                className="h-8 w-8 min-w-8 shrink-0 text-default-400 hover:text-default-600"
                                onPress={() => {
                                    if (usageLimitReached) return;
                                    startRecording();
                                }}
                                isDisabled={isLoading || isExtracting || usageLimitReached}
                                aria-label="Start voice input"
                            >
                                <Icon icon="lucide:mic" width={17} height={17} />
                            </Button>
                        </DarkTooltip>
                    )}

                    {showSmartRoutingToggle && (
                        <DarkTooltip
                            content={
                                smartRoutingEnabled
                                    ? 'Planning Mode Enabled'
                                    : 'Planning Mode Disabled'
                            }
                            size="sm"
                            delay={400}
                        >
                            <Button
                                isIconOnly
                                variant="light"
                                radius="full"
                                size="sm"
                                className={`h-8 w-8 min-w-8 shrink-0 transition-colors ${
                                    smartRoutingEnabled
                                        ? 'bg-primary/15 text-primary ring-1 ring-primary/25 data-[hover=true]:bg-primary/20'
                                        : 'text-default-400 hover:text-default-600'
                                }`}
                                onPress={() => setSmartRoutingEnabled((v) => !v)}
                                aria-label={
                                    smartRoutingEnabled
                                        ? 'Disable Planning Mode'
                                        : 'Enable Planning Mode'
                                }
                                aria-pressed={smartRoutingEnabled}
                            >
                                <Icon
                                    icon={smartRoutingEnabled ? 'lucide:route' : 'lucide:route-off'}
                                    width={17}
                                    height={17}
                                />
                            </Button>
                        </DarkTooltip>
                    )}

                    {isLoading ? (
                        <Button
                            isIconOnly
                            variant="light"
                            radius="full"
                            size="sm"
                            onPress={handleStop}
                            className="h-8 w-8 min-w-8 text-default-500 hover:bg-default-100 hover:text-default-700"
                        >
                            <Icon icon="lucide:square" width={14} height={14} />
                        </Button>
                    ) : (
                        SHOW_SEND_BUTTON && (
                            <Button
                                type="submit"
                                isIconOnly
                                isDisabled={!message.trim() || isExtracting || usageLimitReached}
                                variant="light"
                                radius="full"
                                size="sm"
                                className="h-8 w-8 min-w-8 bg-transparent"
                            >
                                <Icon
                                    icon="lucide:send"
                                    width={18}
                                    height={18}
                                    className={`transition-colors ${
                                        message.trim() && !isExtracting && !usageLimitReached
                                            ? 'text-foreground/70'
                                            : 'text-foreground/40'
                                    }`}
                                />
                            </Button>
                        )
                    )}
                </>
            )}
        </div>
    );

    return (
        <div className={`relative flex flex-col gap-3 ${className}`}>
            <AnimatePresence>
                {showScrollButton && (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.2 }}
                        className="absolute -top-12 left-1/2 z-10 -translate-x-1/2"
                    >
                        <Button
                            isIconOnly
                            size="sm"
                            radius="full"
                            variant="bordered"
                            className="h-8 w-8 border-default-300 bg-content1 shadow-lg hover:bg-default-100 dark:border-default-200"
                            onPress={onScrollToBottom}
                        >
                            <Icon
                                icon="lucide:chevron-down"
                                width={16}
                                height={16}
                                className="text-default-600"
                            />
                        </Button>
                    </motion.div>
                )}
            </AnimatePresence>

            {aboveInput}

            <form onSubmit={handleSubmit} {...getRootProps()}>
                <input {...getInputProps()} />

                <div
                    style={compact ? undefined : { width, height }}
                    className={`relative flex flex-col rounded-3xl border bg-white transition-all duration-200 focus-within:border-primary dark:bg-default-50/90 ${
                        compact ? 'justify-center' : 'mx-auto'
                    } ${
                        isDragActive || isDragOver
                            ? 'border-dashed border-primary bg-primary-50/20 dark:bg-primary-900/10'
                            : previewText
                              ? 'border-primary'
                              : 'border-default-300 dark:border-default-100'
                    }`}
                >
                    {uploadedFile && (
                        <div className="flex flex-col gap-2 px-4 pt-2">
                            <AnimatePresence mode="popLayout">
                                <motion.div
                                    key="uploaded-file"
                                    layout
                                    initial={{ opacity: 0, scale: 0.8 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.8 }}
                                    transition={{ duration: 0.15 }}
                                    className="flex flex-col gap-1"
                                >
                                    <Chip
                                        variant="flat"
                                        startContent={
                                            isExtracting ? (
                                                <Icon
                                                    icon="lucide:loader-2"
                                                    width={14}
                                                    height={14}
                                                    className="animate-spin text-primary"
                                                />
                                            ) : (
                                                <Icon
                                                    icon={
                                                        extractionProgress?.stage === 'complete'
                                                            ? 'lucide:check-circle'
                                                            : 'lucide:file-text'
                                                    }
                                                    width={14}
                                                    height={14}
                                                    className="text-primary"
                                                />
                                            )
                                        }
                                        onClose={!isExtracting ? handleFileRemove : undefined}
                                        classNames={{
                                            base: 'bg-primary-100/60 dark:bg-primary-900/30 hover:bg-primary-200/60 dark:hover:bg-primary-800/40 transition-colors cursor-default',
                                            content:
                                                'text-mini font-medium text-primary-700 dark:text-primary-300 px-1 max-w-[200px] truncate',
                                            closeButton:
                                                'text-primary-500 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-200',
                                        }}
                                    >
                                        {uploadedFile.name}
                                    </Chip>
                                    {isExtracting && extractionProgress && (
                                        <motion.div
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: 'auto' }}
                                            exit={{ opacity: 0, height: 0 }}
                                            className="flex flex-col gap-1"
                                        >
                                            <div className="h-1 w-full overflow-hidden rounded-full bg-default-200">
                                                <motion.div
                                                    className="h-full bg-primary"
                                                    initial={{ width: 0 }}
                                                    animate={{
                                                        width: `${extractionProgress.progress}%`,
                                                    }}
                                                    transition={{ duration: 0.2 }}
                                                />
                                            </div>
                                            <span className="text-tiny text-default-400">
                                                {extractionProgress.message}
                                            </span>
                                        </motion.div>
                                    )}
                                </motion.div>
                            </AnimatePresence>
                        </div>
                    )}

                    <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
                        {isRecording
                            ? 'Voice recording started'
                            : isTranscribing
                              ? 'Transcribing audio'
                              : ''}
                    </div>

                    {compact ? (
                        <div className="flex items-center gap-1.5 px-4 py-2">
                            {attachButton}
                            {textareaOrVisualizer}
                            {iconCluster}
                        </div>
                    ) : (
                        <div className="flex flex-1 flex-col px-4 pb-3 pt-4">
                            <div className="flex-1">{textareaOrVisualizer}</div>
                            <div className="flex items-center gap-1.5 pt-2">
                                {attachButton}
                                <div className="ml-auto">{iconCluster}</div>
                            </div>
                        </div>
                    )}

                    {/* Drag overlay */}
                    {(onFileUpload || fileReader) && (isDragActive || isDragOver) && (
                        <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-3xl bg-primary-50/80 dark:bg-primary-900/40">
                            <div className="flex items-center gap-2 text-primary-600 dark:text-primary-300">
                                <Icon icon="lucide:upload" width={20} height={20} />
                                <span className="text-small font-medium">
                                    Drop file here to upload
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            </form>

            {trialStrip && (
                <div
                    className={`flex flex-wrap items-center justify-center gap-1 px-2 text-default-500 ${
                        compact ? 'text-tiny' : 'text-small'
                    }`}
                    role="status"
                    aria-live="polite"
                    aria-label={trialStrip.line}
                >
                    <Icon
                        icon="lucide:gauge"
                        width={compact ? 10 : 12}
                        height={compact ? 10 : 12}
                        className="shrink-0"
                    />
                    <span>{trialStrip.line}</span>
                    <span aria-hidden>·</span>
                    <Link
                        as={NextLink}
                        href={trialStrip.link.href}
                        size="sm"
                        className={compact ? 'text-tiny' : 'text-small'}
                    >
                        {trialStrip.link.label}
                    </Link>
                    <span aria-hidden>·</span>
                    <Link
                        href={trialStrip.upgrade.href}
                        isExternal
                        size="sm"
                        className={`font-semibold ${compact ? 'text-tiny' : 'text-small'}`}
                    >
                        {trialStrip.upgrade.label} →
                    </Link>
                </div>
            )}

            {usageLimit &&
                (compact ? (
                    <div
                        className={`flex items-center justify-center gap-1 px-2 text-tiny ${
                            orgUsageLimitReached ? 'text-danger-500' : 'text-default-500'
                        }`}
                        role="status"
                        aria-live="polite"
                        title={
                            orgUsageLimitReached
                                ? (usageLimit.limitMessage ??
                                  `Organization ${usageUnit} limit reached.`)
                                : `${usageRemaining.toLocaleString()} of ${usageLimit.limit.toLocaleString()} ${usageUnit} remaining`
                        }
                    >
                        <Icon
                            icon={orgUsageLimitReached ? 'lucide:lock' : 'lucide:gauge'}
                            width={10}
                            height={10}
                            className="shrink-0"
                        />
                        <span className="truncate">
                            {orgUsageLimitReached ? (
                                <>
                                    Limit reached · {usageLimit.currentCount.toLocaleString()}/
                                    {usageLimit.limit.toLocaleString()} {usageUnit}
                                </>
                            ) : (
                                <>
                                    <span className="font-medium text-foreground">
                                        {usageRemaining.toLocaleString()}
                                    </span>
                                    /{usageLimit.limit.toLocaleString()} {usageUnit} left
                                </>
                            )}
                        </span>
                    </div>
                ) : (
                    <div
                        className={`flex flex-wrap items-center justify-center gap-1 px-2 text-small ${
                            orgUsageLimitReached ? 'text-danger-500' : 'text-default-500'
                        }`}
                        role="status"
                        aria-live="polite"
                    >
                        <Icon
                            icon={orgUsageLimitReached ? 'lucide:lock' : 'lucide:gauge'}
                            width={12}
                            height={12}
                        />
                        {orgUsageLimitReached ? (
                            <span>
                                {usageLimit.limitMessage ??
                                    `Org ${usageUnit} limit reached (${usageLimit.currentCount.toLocaleString()} / ${usageLimit.limit.toLocaleString()}).`}
                            </span>
                        ) : (
                            <span>
                                <span className="font-medium text-foreground">
                                    {usageRemaining.toLocaleString()}
                                </span>{' '}
                                of{' '}
                                <span className="font-medium text-foreground">
                                    {usageLimit.limit.toLocaleString()}
                                </span>{' '}
                                {usageUnit} remaining for your organization
                            </span>
                        )}
                    </div>
                ))}

            {onFileUpload && (
                <UploadModal
                    uploadItemName={DOCUMENT_UPLOAD_NAME}
                    show={showUploadModal}
                    setShow={setShowUploadModal}
                    allowedFileTypes={[...ALLOWED_DOCUMENT_TYPES]}
                    onUpload={handleModalUpload}
                />
            )}
        </div>
    );
}
